import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AlertUrgency, AlertType, AlertContentType } from '@/enums/Alerts';
import { Alert as ApiAlert } from '@/models/Alerts';
import { AppDispatch } from '@/models/AppDispatch.type';
import AmiraMessage, { AmiraMessageAlert as Alert } from './AmiraMessage';
import { getAlerts, archiveAlert, snoozeAlert, selectAlertsData, setActiveClassroom } from '@/store/slices/AlertsSlice';
import { useAccessToken } from '@/hooks/useAccessToken';
import useSelectedClassroom from '@/hooks/useSelectedClassroom';
import { RootState } from '@/models';
import Speech, { SpeechPassedDownProps } from '@/components/Reader/Speech/Speech';

export interface AmiraAlertContextType {
  activeIndex: number;
  alerts: Alert[];
  isCollapsed: boolean;
  isModalOpen: boolean;
  isMuted: boolean;
  speechProps: SpeechPassedDownProps;
  transcriptInfo: any;
  addAlert: (alert: Omit<Alert, 'id'>) => string;
  onDismiss: (id: string) => void;
  onSnooze: (id: string) => void;
  clearAllAlerts: () => void;
  onMute: () => void;
  onToggleModal: (open: boolean) => void;
  onToggleCollapse: (collapsed: boolean) => void;
  onSlideChange: (index: number) => void;
}

export const AmiraAlertContext = createContext<AmiraAlertContextType | undefined>(undefined);

// Helper function to convert API alerts to AmiraMessage alerts
const mapApiAlertsToAmiraAlerts = (
  apiAlerts: ApiAlert[],
): Alert[] => apiAlerts.map(({
  id,
  title = '',
  studentId: studentIds = [],
  type,
  attentionLevel: urgency,
  groupedAlertIds: alertIds,
  contentType
}) => ({
  id,
  title,
  alertIds,
  studentIds,
  urgency: urgency as AlertUrgency,
  type: type as AlertType,
  contentType: contentType as AlertContentType,
}));

export const AmiraMessageProvider: React.FC<{ children: React.ReactNode; initialCollapsed?: boolean }> = ({
  children,
  initialCollapsed = false
}) => {
  const CHAT_WIDGET_SELECTOR = '#amira-chatbot-widget'; // The primary chatbot container
  const CHAT_TOGGLE_SELECTOR = `${CHAT_WIDGET_SELECTOR} .acb-chat-toggle-button`; // The trigger for the chat open/close behavior
  const CHAT_CONTAINER_SELECTOR = `${CHAT_WIDGET_SELECTOR} .acb-chat-container`; // The container for the chat conversation

  const dispatch = useDispatch<AppDispatch>();
  const { alerts: alertsState } = useSelector(( state: RootState ) => state.alerts);
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMuted, setIsMuted] = useState<boolean>(() => {
    const savedMute = localStorage.getItem('amiraMessagesMuted');
    return savedMute ? JSON.parse(savedMute) : false;
  });
  const [isCollapsed, setCollapsed] = useState(initialCollapsed);
  const accessToken = useAccessToken();
  const { classroomId } = useSelectedClassroom();

  useEffect(() => {
    const observer = new MutationObserver((_, obs) => {
      const toggleButton = document.querySelector(CHAT_TOGGLE_SELECTOR);
      if (toggleButton) {
        toggleButton.addEventListener('click', () => {
          if (!isCollapsed && (document.querySelector(CHAT_CONTAINER_SELECTOR) as HTMLElement).style.display === 'none') setCollapsed(true);
        });
        obs.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [isCollapsed]);

  // Fetch alerts from the API when the access token becomes available
  useEffect(() => {
    if (accessToken) {
      dispatch(getAlerts());
    }
  }, [dispatch, accessToken]);

  // Update local alerts when Redux store changes
  useEffect(() => {
    if (alertsState && Array.isArray(alertsState)) {
      setAlerts(mapApiAlertsToAmiraAlerts(alertsState));
    }
  }, [alertsState]);

  useEffect(() => {
    if (!initialCollapsed) setCollapsed(alerts.length === 0);
  }, [alerts]);

  // Save mute preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('amiraMessagesMuted', JSON.stringify(isMuted));
  }, [isMuted]);

  useEffect(() => {
    if (classroomId) {
      dispatch(setActiveClassroom({ classroomId }));
    }
  }, [dispatch, classroomId]);

  const bindings = {
    alerts,
    isMuted,
    isCollapsed,
    activeIndex,
    isModalOpen,
    addAlert: (alert: Omit<Alert, 'id'>) => {
      const id = Date.now().toString();
      setAlerts(prevAlerts => [...prevAlerts, { ...alert, id }]);
      return id;
    },
    clearAllAlerts: () => {
      // Only archive server alerts
      alerts.forEach(alert => {
        if (!/^\d+$/.test(alert.id)) {
          dispatch(archiveAlert({ alertId: alert.id }));
        }
      });
      setCollapsed(true);
      setAlerts([]);
      setActiveIndex(0);
    },
    onDismiss: (id: string) => {
      setAlerts(prevAlerts => prevAlerts.filter(alert => alert.id !== id));
      // Only archive server alerts, not local alerts
      if (!/^\d+$/.test(id)) {
        dispatch(archiveAlert({ alertId: id }));
      }
    },
    onMute: () => setIsMuted(prev => !prev),
    onSnooze: (id: string) => {
      // Handle active index updates when snoozing alerts
      const snoozedIndex = alerts.findIndex(alert => alert.id === id);
      if (
        snoozedIndex <= activeIndex
        && alerts.length > 1
        && (
          activeIndex === alerts.length - 1
          || snoozedIndex < activeIndex
        )
      ) {
        setActiveIndex(activeIndex - 1);
      }
      setAlerts(prevAlerts => prevAlerts.filter(alert => alert.id !== id));
      dispatch(snoozeAlert({ alertId: id }));
    },
    onSlideChange: (index: number) => setActiveIndex(index),
    onToggleCollapse: (collapsed: boolean) => {
      const chatContainer = document.querySelector(CHAT_CONTAINER_SELECTOR) as HTMLElement;
      const chatTrigger = document.querySelector(CHAT_TOGGLE_SELECTOR) as HTMLElement;
      if (!collapsed && chatContainer?.style.display === 'block') chatTrigger.click();
      setCollapsed(collapsed)
    },
    onToggleModal: (open: boolean) => setIsModalOpen(open),
  };

  return (
    <Speech config={{}}>
      {(props, transcriptInfo) => (
      <AmiraAlertContext.Provider value={{...bindings, speechProps: props, transcriptInfo}}>
        {children} 
        <AmiraMessage />
      </AmiraAlertContext.Provider>)}
    </Speech>
  );
};

export const useAmiraMessage = (): AmiraAlertContextType => {
  const context = useContext(AmiraAlertContext);
  if (context === undefined) {
    throw new Error('useAmiraMessage must be used within an AmiraMessageProvider');
  }
  return context;
};